import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router';
import { useAuth } from './AuthContext';
import { getReleaseNotes, markReleaseNotesSeen } from '../Helpers/helpApi';

export const KnowledgebaseContext = createContext();

export function KnowledgebaseProvider({ children }) {
  const auth = useAuth();
  const history = useHistory();
  const helpWidgetRef = useRef(null); // ref to return focus to on close

  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState('search');
  const [activeArticleId, setActiveArticleId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [unreadReleaseNotes, setUnreadReleaseNotes] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [tourProgress, setTourProgress] = useState({});

  // Check for unread release notes on mount (when user is logged in)
  React.useEffect(() => {
    if (!auth?.activeUser?.accessToken) return;
    const checkUnread = async () => {
      try {
        const response = await getReleaseNotes(auth.activeUser.accessToken);
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          // Check localStorage for last seen version
          const lastSeen = localStorage.getItem('help_last_seen_version');
          const latestVersion = response.data[0]?.version;
          if (latestVersion && lastSeen !== latestVersion) {
            setUnreadReleaseNotes(true);
          }
        }
      } catch (e) {
        // Silently fail — unread badge is non-critical
      }
    };
    checkUnread();
  }, [auth?.activeUser?.accessToken]);

  const openPanel = useCallback((view = 'search') => {
    // Guard: require valid auth
    if (!auth?.activeUser?.accessToken) {
      history.push('/login');
      return;
    }
    setActiveView(view);
    setIsOpen(true);
  }, [auth, history]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    // Return focus to the help widget trigger
    setTimeout(() => {
      if (helpWidgetRef.current) {
        helpWidgetRef.current.focus();
      }
    }, 100);
  }, []);

  const openArticle = useCallback((articleId) => {
    setActiveArticleId(articleId);
    setActiveView('article');
    if (!isOpen) {
      openPanel('article');
    }
  }, [isOpen, openPanel]);

  const clearUnreadBadge = useCallback(async () => {
    setUnreadReleaseNotes(false);
    if (auth?.activeUser?.accessToken) {
      try {
        await markReleaseNotesSeen(auth.activeUser.accessToken);
        // Also update localStorage
        const response = await getReleaseNotes(auth.activeUser.accessToken);
        if (response?.data?.[0]?.version) {
          localStorage.setItem('help_last_seen_version', response.data[0].version);
        }
      } catch (e) {
        // Silently fail
      }
    }
  }, [auth]);

  // Global '?' key handler removed — no custom keyboard shortcuts in this app

  const value = {
    isOpen,
    activeView,
    activeArticleId,
    searchQuery,
    searchResults,
    isSearching,
    unreadReleaseNotes,
    onboardingStatus,
    tourProgress,
    helpWidgetRef,
    openPanel,
    closePanel,
    openArticle,
    setActiveView,
    setSearchQuery,
    setSearchResults,
    setIsSearching,
    setOnboardingStatus,
    setTourProgress,
    clearUnreadBadge,
  };

  return (
    <KnowledgebaseContext.Provider value={value}>
      {children}
    </KnowledgebaseContext.Provider>
  );
}

export const useKnowledgebase = () => useContext(KnowledgebaseContext);
